/**
 * CodeEditor Component
 * Advanced code editor for viewing and editing generated code
 */

import { useState, useEffect } from 'react';
import { Textarea, Group, Badge, Text, Box } from '@mantine/core';
import { IconCode } from '@tabler/icons-react';

export interface CodeEditorProps {
  value: string;
  onChange?: (newCode: string) => void;
  language?: string;
  readOnly?: boolean;
  height?: string | number;
}

export default function CodeEditor({ 
  value, 
  onChange,
  language = 'html',
  readOnly = false,
  height = 400
}: CodeEditorProps) {
  const [localValue, setLocalValue] = useState(value);
  
  useEffect(() => {
    setLocalValue(value);
  }, [value]);
  
  const handleChange = (newValue: string) => {
    setLocalValue(newValue);
    onChange?.(newValue);
  };
  
  const lines = localValue.split('\n').length;
  const characters = localValue.length;
  
  return (
    <Box>
      {/* Editor Header */}
      <Group justify="space-between" mb="sm">
        <Group gap="xs">
          <IconCode size={16} />
          <Badge size="sm" variant="outline">
            {language.toUpperCase()}
          </Badge>
          {readOnly && (
            <Badge size="sm" color="gray">
              Read Only
            </Badge>
          )}
        </Group>
        <Text size="xs" color="dimmed">
          {lines} lines • {characters} chars
        </Text>
      </Group>
      
      {/* Code Editor */}
      <Textarea
        value={localValue}
        onChange={(e) => !readOnly && handleChange(e.currentTarget.value)}
        readOnly={readOnly}
        autosize={false}
        minRows={20}
        maxRows={50}
        placeholder={`Enter ${language} code here...`}
        styles={{
          input: {
            fontFamily: 'Monaco, Menlo, "Ubuntu Mono", monospace',
            fontSize: '12px',
            lineHeight: 1.5,
            backgroundColor: readOnly ? 'var(--mantine-color-gray-0)' : 'white',
            minHeight: typeof height === 'number' ? `${height}px` : height,
            maxHeight: typeof height === 'number' ? `${height}px` : height,
            overflow: 'auto'
          }
        }}
        spellCheck={false}
      />
    </Box>
  );
}